/** @jsxImportSource ./simboluri-jsx */
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import { FemModel, IncarcariAplicare } from "./tipuri";

// Cum ajung încărcările și masele în OpenSees — văzut pe modelul real.
//
// Sunt trei mecanisme distincte, și confuzia dintre ele e sursa celor mai
// multe neînțelegeri despre model:
//
//   1. PRESIUNE PE PLACĂ → `ops.load` pe noduri. Un shell nu poate primi
//      „kN/m²" direct: presiunea se transformă în patru forțe nodale p·A/4,
//      câte una în fiecare colț. De asta placa se vede colorată, dar ce
//      primește solverul sunt forțele din noduri.
//   2. ÎNCĂRCARE LINIARĂ PE BARĂ → `ops.eleLoad -type -beamUniform`. Grinzile
//      (greutatea inimii + pereții de deasupra), stâlpii (greutatea proprie,
//      pe axa lor locală) și grinzile de fundare (infrastructura + pereții
//      parterului). Bara se colorează după kN/ml.
//   3. MASĂ → `ops.mass` per nod, în trei translații. Masa NU e încărcarea
//      împărțită la g: utila intră cu ψ_E, stâlpii și pereții se împart pe
//      jumătate între planșee, iar infrastructura e exclusă (EN 1998-1
//      §4.3.3.2.2). Bilele se desenează în noduri, mărimea ∝ masă; nodurile
//      de infrastructură apar GOALE — sunt greutate, nu masă seismică.

export type Mod3D = { fel: "incarcare"; caz: string } | { fel: "masa" };

// culoarea fiecărui caz în scenă — aceeași în fișa cazului (`FisaIncarcare`),
// ca simbolul din panou și suprafața aprinsă în 3D să fie recunoscute drept
// același lucru
export const CULOARE_CAZ: Record<string, number> = {
  G1: 0x7a8ca3, G4: 0x7d8788, G2a: 0x3f9e98, G2b: 0x8fcfca, G2c: 0x1f6b67,
  G3: 0x8ba888, Q1: 0xd6a5a5, Q2: 0xb97f7f, S: 0x9fc2d8,
};

export default function Incarcari3D({
  model,
  aplicare,
  mod,
  mase,
  masaInfra,
  height = 480,
}: {
  model: FemModel;
  aplicare: IncarcariAplicare[];
  mod: Mod3D;
  mase: [number, number][];
  masaInfra: [number, number][];
  height?: number;
}) {
  const mount = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = mount.current;
    if (!el || !model?.noduri?.length) return;

    const idx = new Map(model.noduri.map((n, i) => [n.id, i]));
    const P = model.noduri.map((n) => [n.x_m, n.y_m, n.z_m]);
    const xs = P.map((p) => p[0]);
    const ys = P.map((p) => p[1]);
    const zs = P.map((p) => p[2]);
    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;
    const cz = (Math.min(...zs) + Math.max(...zs)) / 2;
    const span = Math.max(
      Math.max(...xs) - Math.min(...xs),
      Math.max(...ys) - Math.min(...ys),
      Math.max(...zs) - Math.min(...zs),
      1
    );

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf4f6fa);
    const camera = new THREE.PerspectiveCamera(45, 1, 0.05, span * 20);
    camera.up.set(0, 0, 1);
    camera.position.set(cx + span * 0.95, cy - span * 1.15, cz + span * 0.8);
    const renderer = new THREE.WebGLRenderer({
      antialias: true, preserveDrawingBuffer: true,
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    el.appendChild(renderer.domElement);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(cx, cy, cz);
    controls.update();
    scene.add(new THREE.AmbientLight(0xffffff, 0.95));

    const V = (i: number) => new THREE.Vector3(P[i][0], P[i][1], P[i][2]);

    // --- scheletul: toate barele, subțire și gri ---------------------------
    const matGri = new THREE.LineBasicMaterial({
      color: 0xc3cbd8, transparent: true, opacity: 0.9,
    });
    const bare: { eid: number; ln: number[] }[] = [];
    model.grinzi.forEach((g) =>
      (g.element_ids ?? []).forEach((eid, k) => {
        const a = g.noduri[k];
        const b = g.noduri[k + 1];
        if (a != null && b != null) bare.push({ eid, ln: [idx.get(a)!, idx.get(b)!] });
      })
    );
    model.stalpi.forEach((s) => {
      if (s.element_id != null) {
        bare.push({ eid: s.element_id, ln: [idx.get(s.nod_jos)!, idx.get(s.nod_sus)!] });
      }
    });
    (model.elemente ?? []).forEach((e) => {
      if ((e.tip as string) === "grinda_fundare") {
        bare.push({ eid: e.id, ln: [idx.get(e.nod_i)!, idx.get(e.nod_j)!] });
      }
    });
    bare.forEach(({ ln }) => {
      if (ln.some((i) => i == null)) return;
      scene.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints(ln.map(V)), matGri));
    });

    // --- placa: quad-uri, umplute doar în modul „încărcare" ----------------
    const shells = model.shells ?? [];
    const ap = mod.fel === "incarcare"
      ? aplicare.find((a) => a.caz === mod.caz)
      : undefined;
    const pPlaca = ap?.placa ?? null;
    const pMax = pPlaca
      ? Math.max(...Object.values(pPlaca.pe_nivel ?? {}).map(Number), 1e-9)
      : 0;

    if (shells.length) {
      // muchiile plăcii, ca reper (întotdeauna)
      const muchii: number[] = [];
      const vazute = new Set<string>();
      shells.forEach((sh) => {
        for (let k = 0; k < 4; k++) {
          const a = sh.noduri[k];
          const b = sh.noduri[(k + 1) % 4];
          const key = a < b ? `${a},${b}` : `${b},${a}`;
          if (vazute.has(key)) continue;
          vazute.add(key);
          const ia = idx.get(a);
          const ib = idx.get(b);
          if (ia != null && ib != null) muchii.push(ia, ib);
        }
      });
      const geoM = new THREE.BufferGeometry();
      geoM.setAttribute("position", new THREE.Float32BufferAttribute(
        muchii.flatMap((i) => P[i]), 3));
      scene.add(new THREE.LineSegments(geoM, new THREE.LineBasicMaterial({
        color: 0xd8dee9, transparent: true, opacity: 0.55,
      })));

      if (pPlaca) {
        // fețele care primesc presiune, colorate după intensitate
        const poz: number[] = [];
        const col: number[] = [];
        const baza = new THREE.Color(CULOARE_CAZ[mod.fel === "incarcare"
          ? mod.caz : "G1"] ?? 0x7a8ca3);
        shells.forEach((sh) => {
          const p = Number((pPlaca.pe_nivel ?? {})[sh.nivel] ?? 0);
          if (!p) return;
          const cat = pPlaca.cat;
          const shCat = (sh as { cat?: string }).cat ?? "A";
          if (cat === "C" && shCat !== "C") return;
          if (cat === "A" && shCat === "C") return;
          // zăpada cade numai pe celulele fără placă deasupra: la un etaj
          // retras, nivelul e încărcat pe terasă și liber sub retragere, deci
          // «tot nivelul» ar desena o presiune care nu există
          if (pPlaca.doar_expus && !sh.expus) return;
          const q = sh.noduri.map((n) => idx.get(n)).filter((i) => i != null) as number[];
          if (q.length < 4) return;
          const c = baza.clone().lerp(new THREE.Color(0xffffff),
                                      0.65 * (1 - p / pMax));
          [[0, 1, 2], [0, 2, 3]].forEach((tri) =>
            tri.forEach((k) => {
              poz.push(...P[q[k]]);
              col.push(c.r, c.g, c.b);
            })
          );
        });
        if (poz.length) {
          const geo = new THREE.BufferGeometry();
          geo.setAttribute("position", new THREE.Float32BufferAttribute(poz, 3));
          geo.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
          scene.add(new THREE.Mesh(geo, new THREE.MeshBasicMaterial({
            vertexColors: true, side: THREE.DoubleSide,
            transparent: true, opacity: 0.85,
          })));
        }
      }
    }

    // --- barele ÎNCĂRCATE: linie groasă colorată după kN/ml ----------------
    if (mod.fel === "incarcare") {
      const linii = ap?.linii_kn_m ?? {};
      const qMax = Math.max(...Object.values(linii).map(Number), 1e-9);
      const baza = new THREE.Color(CULOARE_CAZ[mod.caz] ?? 0x7a8ca3);
      const poz: number[] = [];
      const col: number[] = [];
      bare.forEach(({ eid, ln }) => {
        const q = Number(linii[String(eid)] ?? 0);
        if (!q || ln.some((i) => i == null)) return;
        const c = baza.clone().lerp(new THREE.Color(0x2b2b2b),
                                    0.45 * (q / qMax));
        // un „tub" ieftin: bara desenată de 3 ori, cu offseturi mici, ca să
        // pară mai grasă decât linewidth-ul (ignorat de WebGL pe multe GPU)
        for (const d of [0, 0.035, -0.035]) {
          poz.push(P[ln[0]][0], P[ln[0]][1], P[ln[0]][2] + d);
          poz.push(P[ln[1]][0], P[ln[1]][1], P[ln[1]][2] + d);
          col.push(c.r, c.g, c.b, c.r, c.g, c.b);
        }
      });
      if (poz.length) {
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.Float32BufferAttribute(poz, 3));
        geo.setAttribute("color", new THREE.Float32BufferAttribute(col, 3));
        scene.add(new THREE.LineSegments(geo, new THREE.LineBasicMaterial({
          vertexColors: true,
        })));
      }
    }

    // --- masele: bile în noduri, raza ∝ masa^(1/3) -------------------------
    if (mod.fel === "masa") {
      const m = new Map(mase.map(([n, v]) => [n, v]));
      const mi = new Map(masaInfra.map(([n, v]) => [n, v]));
      const mMax = Math.max(...mase.map(([, v]) => v),
                            ...masaInfra.map(([, v]) => v), 1e-9);
      const rMax = span * 0.012;
      const sfera = new THREE.SphereGeometry(1, 10, 8);
      const adauga = (harta: Map<number, number>, culoare: number,
                      wire: boolean) => {
        const mat = wire
          ? new THREE.MeshBasicMaterial({ color: culoare, wireframe: true })
          : new THREE.MeshBasicMaterial({ color: culoare });
        const inst = new THREE.InstancedMesh(sfera, mat, harta.size);
        let k = 0;
        const mx = new THREE.Matrix4();
        harta.forEach((v, nid) => {
          const i = idx.get(nid);
          if (i == null || v <= 0) return;
          const r = rMax * Math.cbrt(v / mMax);
          mx.makeScale(r, r, r);
          mx.setPosition(P[i][0], P[i][1], P[i][2]);
          inst.setMatrixAt(k++, mx);
        });
        inst.count = k;
        if (k) scene.add(inst);
      };
      adauga(m, 0x2f6feb, false);        // masa seismică — plin
      adauga(mi, 0x9aa4b2, true);        // infrastructura — gol (nu e masă)
    }

    const grid = new THREE.GridHelper(Math.ceil(span * 1.4),
                                      Math.ceil(span * 1.4), 0xd5dce8, 0xe8edf5);
    grid.rotation.x = Math.PI / 2;
    grid.position.set(cx, cy, -0.02);
    scene.add(grid);

    const render = () => renderer.render(scene, camera);
    controls.addEventListener("change", render);
    const resize = () => {
      const w = el.clientWidth || 900;
      const h = el.clientHeight || height;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      render();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    window.addEventListener("resize", resize);
    resize();

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", resize);
      controls.dispose();
      renderer.dispose();
      el.removeChild(renderer.domElement);
      scene.traverse((o) => {
        const mm = o as THREE.Mesh;
        if (mm.geometry) mm.geometry.dispose();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model, aplicare, mod.fel, mod.fel === "incarcare" ? mod.caz : "", mase]);

  return <div ref={mount} className="fem-canvas" style={{ height }} />;
}
